/**
 * Piezas de correo de trabajo (BHM-CLD-REQ-043): conexión, «Prioritario hoy», hilo, vínculos y borradores.
 * Estados vacíos reales; nunca datos de ejemplo.
 */
import { useEffect, useState } from 'react';
import { taskHierarchyLabel } from '../tasks/taskTypes';
import { api } from '../../utils/api';
import { workApi, errMsg, type ProjectRow, type TaskRow } from '../../stores/workApi';
import { mailApi, mailErrorCode, priorityPhrase, PRIORITY_CLS, PRIORITY_LABEL, SIGNAL_LABEL, STATUS_LABEL, type Candidates, type MailDraft, type MailItem, type MailStatus, type MailSummary, type ThreadView } from '../../stores/mailApi';
import { Icon, Pill, ErrorBanner, inputCls, btnGhost, btnPrimary } from '../work/WorkUi';

export const CONNECT_COPY = 'Conecta tu correo corporativo para completar la configuración de BarmetHub.';

export function MailConnectCard({ status, onChanged, compact }: { status: MailStatus | null; onChanged?: () => void; compact?: boolean }) {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!status) return null;
  const connect = async () => {
    setBusy(true); setErr(null);
    try { const { url } = await mailApi.connect(); window.location.assign(url); } catch (x) { setErr(errMsg(x)); setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true); setErr(null);
    try { await mailApi.disconnect(); onChanged?.(); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  if (status.status === 'CONNECTED') {
    return compact ? null : (
      <div className="flex flex-wrap items-center gap-3 text-xs text-on-surface-variant" data-testid="mail-connected">
        <Pill className="border-[#10B981]/40 text-[#10B981]">Correo conectado</Pill>
        <span>{status.email}</span>
        {status.lastSyncAt && <span>· sincronizado {new Date(status.lastSyncAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>}
        <button className="text-outline underline-offset-2 hover:underline" onClick={disconnect} disabled={busy}>Desconectar</button>
      </div>
    );
  }
  const reauth = status.status === 'REAUTH_REQUIRED';
  return (
    <section className="space-y-2 rounded-xl border border-[#FF5E1E]/40 bg-[#FF5E1E]/5 p-4" data-testid="mail-connect" data-status={status.status} aria-label="Conectar correo">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-on-surface">{reauth ? 'Tu correo necesita volver a autorizarse' : CONNECT_COPY}</p>
          <p className="text-xs text-on-surface-variant">
            {status.configured
              ? `Cuenta Google Workspace de @${status.domain}. BarmetHub no guarda tu contraseña y no envía nada sin tu confirmación.`
              : 'La conexión con Google Workspace está pendiente de activación por dirección.'}
            {status.lastError && status.status !== 'DISCONNECTED' ? ` · ${STATUS_LABEL[status.status]}: ${status.lastError}` : ''}
          </p>
        </div>
        <button className={btnPrimary} onClick={connect} disabled={busy || !status.configured} data-testid="mail-connect-btn">
          <Icon name="mail" className="text-base" />{reauth ? 'Volver a autorizar' : 'Conectar Google Workspace'}
        </button>
      </div>
      <ErrorBanner message={err} onClose={() => setErr(null)} />
    </section>
  );
}

export function PriorityToday({ summary, lowValue, spam }: { summary: MailSummary; lowValue: number; spam: number }) {
  return (
    <div className="space-y-2" data-testid="priority-today">
      <p className="text-on-surface">{priorityPhrase(summary)}</p>
      <div className="flex flex-wrap gap-2 text-xs">
        {summary.urgent > 0 && <Pill className={PRIORITY_CLS.URGENTE}>{summary.urgent} urgente{summary.urgent > 1 ? 's' : ''}</Pill>}
        {summary.important > 0 && <Pill className={PRIORITY_CLS.IMPORTANTE}>{summary.important} importante{summary.important > 1 ? 's' : ''}</Pill>}
        {summary.clientWaiting > 0 && <Pill className="border-[#F59E0B]/40 text-[#F59E0B]">{summary.clientWaiting} cliente{summary.clientWaiting > 1 ? 's' : ''} esperando</Pill>}
        <span className="text-outline">Fuera de la bandeja: {lowValue} de bajo valor · {spam} spam</span>
      </div>
    </div>
  );
}

export function MailRow({ m, active, onOpen }: { m: MailItem; active?: boolean; onOpen: () => void }) {
  return (
    <li>
      <button onClick={onOpen} className={`w-full rounded-xl border px-4 py-3 text-left transition ${active ? 'border-[#FF5E1E]/60 bg-surface-container-high' : 'border-outline-variant bg-surface-container-high/40 hover:bg-surface-container-high'}`} data-testid="mail-item" data-priority={m.priority}>
        <div className="flex items-center gap-2">
          <Pill className={PRIORITY_CLS[m.priority]}>{PRIORITY_LABEL[m.priority]}</Pill>
          <span className={`min-w-0 flex-1 truncate text-sm ${m.unread ? 'font-semibold text-on-surface' : 'text-on-surface'}`}>{m.subject}</span>
          <span className="shrink-0 text-[11px] text-outline">{new Date(m.receivedAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <p className="mt-1 truncate text-xs text-on-surface-variant">{m.employee ? `${m.employee.name} · ` : ''}{m.from.name ? `${m.from.name} <${m.from.email}>` : m.from.email} — {m.snippet}</p>
        {(m.signals.length > 0 || m.project) && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {m.signals.filter((s) => SIGNAL_LABEL[s] && !['MASIVO', 'AUTOMATICO', 'PROMOCION', 'INTERNO'].includes(s)).map((s) => <span key={s} className="rounded bg-surface-container-highest px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-on-surface-variant">{SIGNAL_LABEL[s]}</span>)}
            {m.project?.name && <span className="rounded bg-[#FF5E1E]/10 px-1.5 py-0.5 text-[10px] text-[#FF8A5B]">{m.project.name}{m.task?.title ? ` · ${m.task.title}` : ''}</span>}
          </div>
        )}
      </button>
    </li>
  );
}

async function downloadAttachment(gmailId: string, att: { attachmentId: string; filename: string }) {
  const r = await api.get(mailApi.attachmentUrl(gmailId, att.attachmentId), { responseType: 'blob' });
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement('a');
  a.href = url; a.download = att.filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function ThreadPanel({ threadId }: { threadId: string }) {
  const [t, setT] = useState<ThreadView | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setT(null); mailApi.thread(threadId).then(setT).catch((x) => setErr(errMsg(x, 'No se pudo abrir la conversación'))); }, [threadId]);
  if (err) return <ErrorBanner message={err} onClose={() => setErr(null)} />;
  if (!t) return <p className="text-sm text-on-surface-variant">Abriendo conversación…</p>;
  return (
    <div className="space-y-3" data-testid="mail-thread">
      {(t.messages ?? []).length === 0 && <p className="text-sm text-on-surface-variant">Conversación vacía.</p>}
      {(t.messages ?? []).map((m) => (
        <article key={m.gmailId} className={`rounded-lg border p-3 ${m.sent ? 'border-[#3b82f6]/30 bg-[#3b82f6]/5' : 'border-outline-variant bg-surface-container-low'}`}>
          <p className="text-xs text-on-surface-variant"><span className="font-medium text-on-surface">{m.from?.name ?? m.from?.email ?? '—'}</span> · {new Date(m.date).toLocaleString('es-ES')}{m.sent ? ' · enviado' : ''}</p>
          <p className="text-[11px] text-outline">Para: {m.to}{m.cc ? ` · Cc: ${m.cc}` : ''}</p>
          <div className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap text-sm text-on-surface">{m.text || '(sin texto)'}</div>
          {(m.attachments ?? []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {m.attachments.map((a) => <button key={a.attachmentId} className={btnGhost} onClick={() => void downloadAttachment(m.gmailId, a).catch(() => setErr('No se pudo descargar el adjunto'))}><Icon name="attach_file" className="text-sm" />{a.filename}</button>)}
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

export function LinkPanel({ m, onDone }: { m: MailItem; onDone: (u: MailItem) => void }) {
  const [cands, setCands] = useState<Candidates | null>(null);
  const [projects, setProjects] = useState<ProjectRow[] | null>(null);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [projectId, setProjectId] = useState(m.project?.id ?? '');
  const [taskId, setTaskId] = useState(m.task?.id ?? '');
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    mailApi.candidates(m.id).then((c) => { setCands(c); if (!m.project && c.kind === 'ONE') setProjectId(c.candidates[0].projectId); }).catch(() => setCands({ kind: 'NONE', candidates: [], message: 'No encuentro un proyecto asociado a este correo. ¿Quieres seleccionar uno?' }));
    workApi.projects().then(setProjects).catch(() => setProjects([]));
  }, [m.id, m.project]);
  useEffect(() => { if (projectId) workApi.projectTasks(projectId).then(setTasks).catch(() => setTasks([])); else setTasks([]); }, [projectId]);
  const save = async () => {
    setErr(null);
    try { onDone(await mailApi.link(m.id, projectId || null, taskId || null)); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-outline-variant p-3" data-testid="mail-link">
      {cands && <p className="text-xs text-on-surface-variant">{cands.message}{(cands.candidates ?? []).length > 0 && ` ${cands.candidates.map((c) => `${c.name} (${c.why})`).join(' · ')}`}</p>}
      <div className="grid gap-2 md:grid-cols-2">
        <select className={inputCls} value={projectId} onChange={(e) => { setProjectId(e.target.value); setTaskId(''); }} aria-label="Proyecto del correo">
          <option value="">Sin proyecto</option>
          {cands?.candidates.map((c) => <option key={`c-${c.projectId}`} value={c.projectId}>★ {c.name}</option>)}
          {projects?.filter((p) => !cands?.candidates.some((c) => c.projectId === p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className={inputCls} value={taskId} onChange={(e) => setTaskId(e.target.value)} disabled={!projectId} aria-label="Tarea del correo">
          <option value="">Sin tarea</option>
          {tasks.map((t) => <option key={t.id} value={t.id}>{taskHierarchyLabel({ projectName: (t as { project?: { name?: string } }).project?.name ?? null, processName: (t as { process?: { name?: string } }).process?.name ?? null, taskCode: (t as { taskCode?: string }).taskCode ?? null, title: t.title })}</option>)}
        </select>
      </div>
      <div className="flex justify-end"><button className={btnPrimary} onClick={save}>Guardar asociación</button></div>
      <ErrorBanner message={err} onClose={() => setErr(null)} />
    </div>
  );
}

export function DraftEditor({ init, onClose, notify }: { init: { replyToId?: string; to?: string[]; subject?: string; body: string; projectId?: string; taskId?: string; executionId?: string; draft?: MailDraft }; onClose: () => void; notify: (m: string) => void }) {
  const [draft, setDraft] = useState<MailDraft | null>(init.draft ?? null);
  const [to, setTo] = useState((init.to ?? init.draft?.toEmails.split(',') ?? []).join(', '));
  const [subject, setSubject] = useState(init.subject ?? init.draft?.subject ?? '');
  const [body, setBody] = useState(init.body);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const toList = to.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
  const save = async (): Promise<MailDraft | null> => {
    setBusy(true); setErr(null);
    try {
      const d = draft
        ? await mailApi.updateDraft(draft.id, { to: toList.length ? toList : undefined, subject: subject || undefined, body })
        : await mailApi.createDraft({ replyToId: init.replyToId, to: toList.length ? toList : undefined, subject: subject || undefined, body, projectId: init.projectId, taskId: init.taskId, executionId: init.executionId });
      setDraft(d); setTo(d.toEmails.split(',').join(', ')); setSubject(d.subject);
      notify('Borrador guardado en tu Gmail');
      return d;
    } catch (x) {
      setErr(mailErrorCode(x) === 'MAIL_NOT_CONNECTED' ? CONNECT_COPY : errMsg(x));
      return null;
    } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true); setErr(null);
    try {
      const d = draft ?? (await save());
      if (!d) return;
      await mailApi.send(d.id);
      notify('Correo enviado desde tu Gmail');
      onClose();
    } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); setConfirming(false); }
  };
  return (
    <section className="space-y-2 rounded-xl border border-outline-variant bg-surface-container-low p-4" data-testid="draft-editor" aria-label="Borrador de correo">
      <div className="flex items-center justify-between"><h3 className="text-sm font-semibold text-on-surface">{init.replyToId ? 'Respuesta' : 'Nuevo correo'}{draft ? ' · borrador guardado en Gmail' : ''}</h3><button onClick={onClose} aria-label="Cerrar borrador"><Icon name="close" className="text-sm text-outline" /></button></div>
      <input className={inputCls} placeholder="Para (vacío = responder al remitente)" value={to} onChange={(e) => setTo(e.target.value)} aria-label="Para" />
      <input className={inputCls} placeholder="Asunto (vacío = Re: asunto original)" value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Asunto" />
      <textarea className={`${inputCls} min-h-[200px]`} value={body} onChange={(e) => setBody(e.target.value)} aria-label="Cuerpo" />
      <div className="flex flex-wrap justify-end gap-2">
        <button className={btnGhost} onClick={() => void save()} disabled={busy || !body.trim()}>Guardar borrador</button>
        <button className={btnPrimary} onClick={() => setConfirming(true)} disabled={busy || !body.trim()} data-testid="draft-send"><Icon name="send" className="text-base" />Enviar…</button>
      </div>
      {confirming && (
        <div className="space-y-2 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3" role="alertdialog" aria-label="Confirmar envío" data-testid="send-confirm">
          <p className="text-sm text-on-surface">Vas a enviar un correo externo desde tu cuenta a <strong>{toList.length ? toList.join(', ') : 'el remitente original'}</strong>. Esta acción no se puede deshacer.</p>
          <div className="flex justify-end gap-2"><button className={btnGhost} onClick={() => setConfirming(false)}>Cancelar</button><button className={btnPrimary} onClick={send} disabled={busy} data-testid="send-confirm-btn">Confirmar y enviar</button></div>
        </div>
      )}
      <ErrorBanner message={err} onClose={() => setErr(null)} />
    </section>
  );
}

/** Aviso global (REQ-043 §6): correo corporativo sin conectar. Se puede ocultar durante la sesión; nunca bloquea BarmetHub. */
export function MailSetupBanner({ enabled }: { enabled: boolean }) {
  const [st, setSt] = useState<MailStatus | null>(null);
  const [hidden, setHidden] = useState(() => { try { return sessionStorage.getItem('bhm-mail-banner') === '1'; } catch { return false; } });
  useEffect(() => { if (enabled && !hidden) mailApi.status().then(setSt).catch(() => setSt(null)); }, [enabled, hidden]);
  if (!enabled || hidden || !st || st.status === 'CONNECTED' || !st.configured) return null;
  return (
    <div className="mx-6 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#FF5E1E]/30 bg-[#FF5E1E]/5 px-4 py-2 text-sm" role="status" data-testid="mail-banner">
      <span className="flex items-center gap-2 text-on-surface"><Icon name="mail" className="text-base text-[#FF5E1E]" />{st.status === 'REAUTH_REQUIRED' ? 'Tu correo corporativo necesita volver a autorizarse.' : CONNECT_COPY}</span>
      <span className="flex gap-2">
        <a href="/nexus?tab=correo" className="rounded-lg bg-[#FF5E1E] px-3 py-1 text-xs font-medium text-white">{st.status === 'REAUTH_REQUIRED' ? 'Volver a autorizar' : 'Conectar Google Workspace'}</a>
        <button aria-label="Ocultar" className="text-on-surface-variant" onClick={() => { setHidden(true); try { sessionStorage.setItem('bhm-mail-banner', '1'); } catch { /* sin storage */ } }}><Icon name="close" className="text-base" /></button>
      </span>
    </div>
  );
}
